import React from 'react'
import { useHistory } from 'react-router-dom'

/**
 * Shared header bar for every page inside the Bid Estimation module.
 *
 * Props:
 * - backTo:      string | null           -- path for back link (shown only when no breadcrumbs)
 * - backLabel:   string                  -- label for the back link (default "Back")
 * - breadcrumbs: [{ label, to? }, ...]   -- breadcrumb trail; last item = current page (bold, no link)
 * - title:       string                  -- page title (Fraunces 24/600)
 * - statusChip:  { label, className }    -- optional status pill (e.g. { label: 'Draft', className: 'pill-warn' })
 *                                           For backward compat, also accepts { label, style }
 * - meta:        string                  -- optional meta line (e.g. "P-2148 . Bid due May 14")
 * - actions:     ReactNode               -- right-side action buttons
 *
 * Uses .page-head / .crumbs / .title-row from hmiReskin.css
 * and .pill / .pill-* from desertFlora.css.
 */
const BidEstimationHeader = ({
  backTo = null,
  backLabel = 'Back',
  breadcrumbs = [],
  title,
  statusChip = null,
  meta = null,
  actions = null
}) => {
  const history = useHistory()

  return (
    <div className="page-head">
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <div className="crumbs">
          {breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1
            return (
              <React.Fragment key={idx}>
                {idx > 0 && <span className="sep">&rsaquo;</span>}
                {isLast || !crumb.to ? (
                  <span className={isLast ? 'here' : ''}>{crumb.label}</span>
                ) : (
                  <a onClick={() => typeof crumb.to === 'function' ? crumb.to() : history.push(crumb.to)}>{crumb.label}</a>
                )}
              </React.Fragment>
            )
          })}
        </div>
      )}

      {/* Title row */}
      <div className="title-row">
        {/* Back link for pages without breadcrumbs */}
        {backTo && breadcrumbs.length === 0 && (
          <a
            onClick={() => history.push(backTo)}
            style={{ color: 'var(--ink-3)', fontSize: 12, cursor: 'pointer', textDecoration: 'none' }}
          >
            &larr; {backLabel}
          </a>
        )}

        <h1>{title}</h1>

        {statusChip && (
          <span
            className={`pill ${statusChip.className || 'pill-neutral'}`}
            style={!statusChip.className && statusChip.style ? statusChip.style : undefined}
          >
            <span className="dot" />
            {statusChip.label}
          </span>
        )}

        {meta && (
          <span className="meta">{meta}</span>
        )}

        {actions && (
          <div className="actions">
            {actions}
          </div>
        )}
      </div>
    </div>
  )
}

export default BidEstimationHeader
